import {
  ArrowRightIcon,
  Box,
  CheckIcon,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKClientDataGrid,
  GKModal,
  GKRadio,
  Grid2,
  RadioGroup,
  Show,
  StatusField,
  Table,
  TableBody,
  TableCell,
  Typography,
} from "components";
import DotStatusField from "components/DotStatusField";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { SingleStockItemInterface } from "interfaces/EquityBasket";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { extraColumnsField, refetchQuery } from "utils/helpers";

const SendRecommendationNotification = ({
  open,
  setOpen,
  selectedRecommendation,
  isRebalanceModal = false,
}: GKModalProps & {
  selectedRecommendation: number;
  isRebalanceModal?: boolean;
}) => {
  const { id } = useParams();

  const [showSubscribersList, setShowSubscribersList] = useState(false);

  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const [selectedSubscriberType, setSelectedSubscriberType] = useState("");

  const { data: subscriberList } = useFetchQuery<{
    data: {
      data: {
        clientName: string;
        clientCode: string;
        clientId: number;
        portfolioName: string;
        portfolioId: number;
        portfolioCode: string;
        phone: string;
        email: string;
      }[];
    };
  }>({
    key: ["GET_SUBSCRIBERS", selectedSubscriberType],
    route: `/model_portfolio/${id}/subscribers_list/?subscriber_type=${selectedSubscriberType}`,
    enabled: !isEmpty(selectedSubscriberType),
  });

  const { data: recommendationDetails } = useFetchQuery<{
    data: SingleStockItemInterface;
  }>({
    key: ["GET_RECOMMENDATION_DETIALS"],
    route: `/model_portfolio/${id}/stock_item/${selectedRecommendation}/?is_recommendation=${!isRebalanceModal}`,
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(`/model_portfolio/${id}/send_notification/`, data),
    onSuccess: () => {
      refetchQuery("GET_REBALANCES");
      refetchQuery("GET_REBALANCES_DETAILS");
    },
  });

  return (
    <GKModal
      open={open}
      width={showSubscribersList ? "80%" : "30%"}
      setOpen={setOpen}
      modalTitle={
        isRebalanceModal
          ? "Rebalance Notification"
          : "Send Recommendation Notification"
      }
      footer={
        <Box display={"flex"} gap={2} justifyContent={"flex-end"}>
          <GKButton
            loading={isLoading}
            endIcon={!showSubscribersList ? <ArrowRightIcon /> : <CheckIcon />}
            variant="contained"
            color="primary"
            onClick={() => {
              if (showSubscribersList) {
                mutate({
                  clientId: selectedUserIds,
                  rebalanceId: selectedRecommendation,
                  isRecommendation: !isRebalanceModal,
                  subscriber_type: selectedSubscriberType,
                });
              } else {
                setShowSubscribersList(true);
              }
            }}
          >
            {!showSubscribersList ? "Next, Select Users" : "Send Notification"}
          </GKButton>
        </Box>
      }
    >
      <Grid2 container spacing={2}>
        <Show.When isTrue={!showSubscribersList}>
          <Show.When isTrue={!isRebalanceModal}>
            <Grid2
              container
              size={12}
              p={2}
              bgcolor={"#F8F8F8"}
              borderRadius={2}
              spacing={1}
            >
              <Grid2 size={6} display={"flex"} alignItems={"center"} gap={1}>
                <StatusField
                  statusString={recommendationDetails?.data?.recommendationType}
                />
                <Typography fontSize={12} color="#828282">
                  {recommendationDetails?.data?.completionDate}
                </Typography>
              </Grid2>
              <Grid2
                size={6}
                display={"flex"}
                justifyContent={"flex-end"}
                alignItems={"center"}
              >
                <DotStatusField
                  statusString={recommendationDetails?.data?.status}
                />
              </Grid2>
              <Grid2 size={12}>
                <Typography variant="primaryDark">
                  {recommendationDetails?.data?.name}
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Table>
                  <TableBody>
                    <TableCell>
                      <Typography
                        color="#828282"
                        fontSize={12}
                        fontWeight={600}
                      >
                        Target
                      </Typography>
                      <Typography fontSize={12} fontWeight={600}>
                        {numberToCurrency(
                          recommendationDetails?.data?.targetPrice,
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="#828282"
                        fontSize={12}
                        fontWeight={600}
                      >
                        Upside Pending
                      </Typography>
                      <Typography fontSize={12} fontWeight={600}>
                        {numberToCurrency(
                          recommendationDetails?.data?.upsidePending,
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="#828282"
                        fontSize={12}
                        fontWeight={600}
                      >
                        Stop Loss
                      </Typography>
                      <Typography fontSize={12} fontWeight={600}>
                        {numberToCurrency(
                          recommendationDetails?.data?.stopLoss,
                        )}
                      </Typography>
                    </TableCell>
                  </TableBody>
                </Table>
              </Grid2>
            </Grid2>
          </Show.When>
          <Grid2 size={12}>
            <FormControl>
              <FormLabel id="demo-radio-buttons-group-label">
                <Typography variant="primaryLight">
                  send notification to:
                </Typography>
              </FormLabel>
              <RadioGroup
                onChange={(e) => {
                  setSelectedSubscriberType(e.target.value);
                }}
              >
                <FormControlLabel
                  value="ACTIVE"
                  control={<GKRadio />}
                  label={"Active Subscribers"}
                  slotProps={{
                    typography: {
                      fontWeight: 600,
                    },
                  }}
                />
                <Typography color="#697586">
                  (Instantly notifies all active subscribers with full details
                  of the new recommendation.)
                </Typography>
                <FormControlLabel
                  value="INACTIVE"
                  control={<GKRadio />}
                  label={"Inactive Subscribers"}
                  slotProps={{
                    typography: {
                      fontWeight: 600,
                    },
                  }}
                />
                <Typography color="#697586">
                  (Sends a teaser alert to inactive users, encouraging them to
                  subscribe to view full recommendation details.)
                </Typography>
                {/* <Show.When isTrue={isRebalanceModal}>
                  <FormControlLabel
                    value="NON_RENEWED"
                    control={<GKRadio />}
                    label={"Non-Renewed Users"}
                    slotProps={{
                      typography: {
                        fontWeight: 600,
                      },
                    }}
                  />
                  <Typography color="#697586">
                    (These users had an active subscription but didn’t renew
                    recently.)
                  </Typography>
                </Show.When> */}
              </RadioGroup>
            </FormControl>
          </Grid2>
        </Show.When>

        <Show.When isTrue={showSubscribersList}>
          <Grid2 size={12}>
            <GKClientDataGrid
              checkboxSelection
              rowSelectionModel={selectedUserIds}
              onRowSelectionModelChange={(params: any) => {
                setSelectedUserIds(params);
              }}
              rows={
                subscriberList?.data?.data?.map((d) => {
                  return {
                    ...d,
                    id: d?.clientId,
                  };
                }) || []
              }
              columns={[
                {
                  ...extraColumnsField("Client Name", "clientName", 0, 1),
                },
                {
                  ...extraColumnsField("client Code", "clientCode", 0, 1),
                },
                {
                  ...extraColumnsField("email", "email", 0, 1),
                },
                {
                  ...extraColumnsField("phone", "phone", 0, 1),
                },
                {
                  ...extraColumnsField("portfolio Code", "portfolioCode", 0, 1),
                },
                {
                  ...extraColumnsField("portfolio Name", "portfolioName", 0, 1),
                },
              ]}
              name="recommendationSendTable"
            />
          </Grid2>
        </Show.When>
      </Grid2>
    </GKModal>
  );
};

export default SendRecommendationNotification;
